import { Fragment, useEffect, useRef, useState } from "react"

// ** Third Party Components
import { Check } from "lucide-react"
import { Locale } from "react-joyride"
import { ArrowDown2 } from "iconsax-reactjs"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { destroyCookie, setCookie } from "nookies"
import { useMutation } from "@tanstack/react-query"

// ** helpers
import { cn } from "@pma/utils/helpers"

// ** Constants & Variables
import { DictType } from "@pma/types"
import { locales } from "@pma/locale/dictionaries"
import { LOGIN_PAGE_ROUTE, SETTING_PAGE_ROUTE, USER_PAGE_ROUTE } from "@pma/utils/page-routes"
import { ACCESS_TOKEN, ADMIN_ROLE_ID, LOCALE, settingBreadcrumbs } from "@pma/utils/constants"
import { usePermissions } from "@pma/hooks/use-permissions"

// ** Services
import { changeUserDefaultLanguageService } from "@pma/services/common"

// ** Store
import { useAuth } from "@pma/stores/auth-store"
import { useCommonStore } from "@pma/stores/common-store"

// ** Custom hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import WorkSpaceDialog from "./WorkSpaceDialog"
import InviteUserDialog from "./InviteUserDialog"
import { showDeletePermissionToast } from "@pma/pages/Project/Task/Common/DeletePermissionToast"

const UserProfile = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { i18n } = useTranslation()
  const { translate, locale } = useTranslator()
  const { authConfig, signOut, signIn } = useAuth()
  const isHebrew = locale === "he"
  const { currentOrg } = usePermissions()

  const userRole = currentOrg?.role

  const dropdownRef = useRef<HTMLDivElement>(null)
  const languageButtonRef = useRef<HTMLButtonElement>(null)

  const { setHeaderBreadcrumbsListAction } = useCommonStore()

  const [isMobile, setIsMobile] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [workSpaceDialog, setWorkSpaceDialog] = useState(false)
  const [inviteUserDialog, setInviteUserDialog] = useState(false)
  const [isLanguageSubmenuOpen, setIsLanguageSubmenuOpen] = useState(false)
  const [submenuPosition, setSubmenuPosition] = useState<"left" | "right">("right")

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
        setIsLanguageSubmenuOpen(false)
      }
    }

    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint
    }

    checkScreenSize()
    window.addEventListener("resize", checkScreenSize)
    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      window.removeEventListener("resize", checkScreenSize)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
    setIsLanguageSubmenuOpen(false)
  }

  const toggleLanguageSubmenu = () => {
    if (!isLanguageSubmenuOpen) {
      // Calculate position when opening submenu
      if (languageButtonRef.current) {
        const rect = languageButtonRef.current.getBoundingClientRect()
        const submenuWidth = 160 // w-40 = 160px
        const viewportWidth = window.innerWidth
        const spaceOnRight = viewportWidth - rect.right

        // If not enough space on the right, position on the left
        if (spaceOnRight < submenuWidth + 10) setSubmenuPosition("left")
        else setSubmenuPosition("right")
      }
    }
    setIsLanguageSubmenuOpen(!isLanguageSubmenuOpen)
  }

  const changeLocale = (locale: Locale) => {
    setCookie(null, LOCALE, locale as string, {
      maxAge: 1000 * 60 * 60 * 24 * 360,
      sameSite: "strict",
      path: "/",
    })
    setIsDropdownOpen(false)
    changeUserLanguageMutation.mutate(locale)
  }

  const changeUserLanguageMutation = useMutation({
    mutationKey: ["user-language"],
    mutationFn: (data: any) => changeUserDefaultLanguageService({ selected_language: data }),
    onSuccess: (data, locale) => {
      i18n.changeLanguage(locale as string)
      signIn({ ...data })
      toast({
        title: translate("Success"),
        description: `${translate("Default language")} ${translate("updated")}`,
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: i18n.t("Error"),
        description: i18n.t(errorResponse || "Unable to update default language"),
        variant: "error",
      })
    },
  })

  const handleLogOutFunction = () => {
    signOut()
    destroyCookie(undefined, ACCESS_TOKEN)
    document.cookie = ACCESS_TOKEN + "=; Max-Age=0"
    navigate(LOGIN_PAGE_ROUTE)
  }

  const handleNavigateSetting = () => {
    if (userRole?.id !== ADMIN_ROLE_ID) {
      showDeletePermissionToast({
        message: translate("You don't have permission to edit settings"),
        actionLabel: translate("Contact Admin"),
        onAction: () => {
          console.log("contact admin")
        },
      })
      return
    }

    if (!location.pathname.includes("setting")) {
      localStorage.setItem("breadcrumbs", JSON.stringify(settingBreadcrumbs))
      setHeaderBreadcrumbsListAction(settingBreadcrumbs)
      navigate(SETTING_PAGE_ROUTE)
      setIsDropdownOpen(false)
    }
  }

  return (
    <Fragment>
      <div className='relative' ref={dropdownRef}>
        <button
          className='h-10 w-10 rounded-full hover:border hover:border-neutral-400 active:border-indigo-500'
          onClick={toggleDropdown}
        >
          <img
            loading='lazy'
            src={`${authConfig?.user_picture?.url || process.env.PUBLIC_URL + "/user-profile.svg"}`}
            className='object-fit h-10 w-10 rounded-full'
          />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div
            className={cn(
              "absolute top-full z-50 mt-2 w-52 rounded-lg border border-gray-200 bg-white py-2 shadow-lg",
              isHebrew ? "left-0" : "right-0",
            )}
          >
            {/* Menu Items */}
            <div className='py-2'>
              <button
                className='flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 active:bg-indigo-100'
                onClick={() => {
                  setIsDropdownOpen(false)
                  navigate(`${USER_PAGE_ROUTE}/${authConfig.id}`)
                }}
              >
                {translate("Profile")}
              </button>

              <button
                onClick={handleNavigateSetting}
                className='flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 active:bg-indigo-100'
              >
                {translate("Settings")}
              </button>
              {/* Language Options - Mobile vs Desktop */}
              {isMobile ? (
                // Mobile: Show language options inline
                <>
                  <div className='px-4 py-2'>
                    <div className='flex items-center gap-3 text-sm text-gray-500'>
                      {translate("Language")}
                    </div>
                  </div>
                  {locales.map((language: DictType, index: number) => (
                    <button
                      key={index}
                      onClick={() => changeLocale(language.locale)}
                      className='flex w-full items-center justify-between px-8 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50'
                    >
                      {translate(language?.name)}
                      {locale === language.locale && <Check className='h-4 w-4 text-indigo-500' />}
                    </button>
                  ))}
                </>
              ) : (
                // Desktop: Show language submenu
                <div className='relative'>
                  <button
                    ref={languageButtonRef}
                    onClick={toggleLanguageSubmenu}
                    className='flex w-full items-center justify-between gap-3 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 active:bg-indigo-100'
                  >
                    <div className='flex items-center gap-3'>{translate("Language")}</div>
                    <div className='flex items-center gap-1'>
                      <span className='text-xs text-gray-500'>
                        {isHebrew ? translate("Hebrew") : "English"}
                      </span>
                      <ArrowDown2
                        className={cn(
                          "h-3 w-3 text-gray-400 transition-transform",
                          isLanguageSubmenuOpen && "rotate-180",
                        )}
                      />
                    </div>
                  </button>

                  {/* Language Submenu */}
                  {isLanguageSubmenuOpen && (
                    <div
                      className={cn(
                        "absolute top-0 z-10 w-40 rounded-lg border border-gray-200 bg-white py-2 shadow-lg",
                        submenuPosition === "right" ? "left-full ml-1" : "right-full mr-1",
                      )}
                    >
                      {locales.map((language: DictType, index: number) => (
                        <button
                          key={index}
                          onClick={() => changeLocale(language.locale)}
                          className='flex w-full items-center justify-between px-8 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 active:bg-indigo-100'
                        >
                          {language?.name}
                          {locale === language.locale && (
                            <Check className='h-4 w-4 text-indigo-500' />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button
                className='flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 active:bg-indigo-100'
                onClick={() => setWorkSpaceDialog(true)}
              >
                {translate("Switch workspace")}
              </button>
              {userRole?.id == ADMIN_ROLE_ID && (
                <button
                  className='flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 active:bg-indigo-100'
                  onClick={() => setInviteUserDialog(true)}
                >
                  {translate("Invite User")}
                </button>
              )}
              <button
                className='flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50'
                onClick={handleLogOutFunction}
              >
                {translate("Logout")}
              </button>
            </div>
          </div>
        )}
      </div>

      {workSpaceDialog && (
        <WorkSpaceDialog
          workSpaceDialog={workSpaceDialog}
          setWorkSpaceDialog={setWorkSpaceDialog}
        />
      )}

      {inviteUserDialog && (
        <InviteUserDialog
          inviteUserDialog={inviteUserDialog}
          setInviteUserDialog={setInviteUserDialog}
        />
      )}
    </Fragment>
  )
}

export default UserProfile
